import { PluginState } from "@intelligems/ig-types/schemas/plugin/plugin/embedded";
import {
  ProxyStateController,
  ProxyStateEventMap,
} from "@src/components/ReverseProxy/ProxyStateController";
import { ReverseProxyURLOptions } from "@src/components/ReverseProxy/ReverseProxyController";

export type PluginStateEventMap = ProxyStateEventMap & {
  version: string | undefined;
};

export type PluginStateURLOptions = ReverseProxyURLOptions;

type ProxyReachability = "probing" | "reachable" | "unreachable";

const PROBE_TIMEOUT_MS = 5_000;

export class PluginStateController<
  Events extends PluginStateEventMap = PluginStateEventMap,
  URLOptions extends PluginStateURLOptions = PluginStateURLOptions,
> extends ProxyStateController<Events, URLOptions> {
  private version: string | undefined;
  private pluginState: PluginState | undefined;
  private proxyReachability: ProxyReachability | undefined;
  private pageLoadId = 0;

  constructor() {
    super();
    this.timeoutDuration = 15_000;
  }

  override update() {
    super.update();
    const version = this.getVersion();
    if (version !== this.version) {
      this.version = version;
      this.emit("version", version);
    }
  }

  override onPluginState(pluginState: PluginState) {
    super.onPluginState(pluginState);
    this.pluginState = pluginState;
    this.update();
  }

  override resetState() {
    super.resetState();
    this.pluginState = undefined;
    this.proxyReachability = undefined;
    this.pageLoadId++;
  }

  protected override onTimeout() {
    if (!this.proxyState && !this.commitPending && this.proxyUrl) {
      void this.probeProxy(this.proxyUrl.origin);
    }
    super.onTimeout();
  }

  /**
   * Nothing of ours reported from the frame, so check whether this browser can
   * reach the proxy at all. The answer tells a blocked network apart from
   * every other load failure, and is dropped once another page load started.
   */
  private async probeProxy(origin: string) {
    const pageLoadId = this.pageLoadId;
    this.proxyReachability = "probing";
    const reachable = await this.isProxyReachable(origin);
    if (pageLoadId !== this.pageLoadId) {
      return;
    }
    this.proxyReachability = reachable ? "reachable" : "unreachable";
    this.update();
  }

  /**
   * A company web filter, a DNS block or an extension makes the fetch fail. A
   * filter's own block page lacks the CORS header nginx sets on `/health`, so
   * it fails too.
   */
  private async isProxyReachable(origin: string): Promise<boolean> {
    try {
      const response = await fetch(new URL("/health", origin), {
        cache: "no-store",
        credentials: "omit",
        signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
      });
      return response.ok && (await response.text()).trim() === "ok";
    } catch {
      return false;
    }
  }

  override getLoadState() {
    if (this.errorFired && this.proxyErrorReport) {
      return {
        type: "error",
        subtype: "proxy",
        proxyError: this.proxyErrorReport,
      } as const;
    }
    if (this.errorFired || this.targetUrl === null) {
      return { type: "error" } as const;
    }
    if (this.pluginState || this.isAuthPageLoaded) {
      return { type: "ready" } as const;
    }
    if (this.timerFired) {
      if (this.commitPending) {
        return { type: "ready" } as const;
      }
      if (!this.proxyState) {
        return this.getProxyErrorState();
      }
      if (!this.pluginState) {
        return { type: "error", subtype: "plugin" } as const;
      }
    }
    return undefined;
  }

  private getProxyErrorState() {
    if (this.proxyReachability === "probing") {
      return undefined;
    }
    if (this.proxyReachability === "unreachable" && this.proxyUrl) {
      const url = new URL(this.proxyUrl.origin);
      if (this.targetUrl) {
        url.searchParams.set("__url__", this.targetUrl.href);
      }
      return {
        type: "error",
        subtype: "blocked",
        url: url.href,
      } as const;
    }
    return { type: "error", subtype: "proxy" } as const;
  }

  protected getVersion() {
    return this.pluginState ? (this.pluginState.version ?? null) : undefined;
  }
}
